import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  Stack,
  List,
  ListIcon,
  useColorModeValue,
  VStack,
  HStack,
  SimpleGrid,
  useToast,
} from '@chakra-ui/react';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import env from 'config/env';
import { loadStripe } from '@stripe/stripe-js';

// Initialize Stripe
const stripePromise = env.STRIPE_PUBLIC_KEY 
  ? loadStripe(env.STRIPE_PUBLIC_KEY)
  : Promise.reject(new Error('Stripe public key is not configured'));

interface PricingTier {
  name: string;
  price: string;
  period: string;
  features: Array<{
    text: string;
    included: boolean;
  }>;
  buttonText: string;
  isPopular?: boolean;
  priceId?: string | null;
}

const pricingTiers: PricingTier[] = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    features: [
      { text: 'Unlimited journal entries', included: true },
      { text: 'Strong encryption for all entries', included: true },
      { text: 'Basic mood tracking', included: true },
      { text: 'Tags for organization', included: true },
      { text: '15 AI chats per month', included: true },
      { text: 'Custom fields', included: false },
      { text: 'Unlimited AI chats', included: false },
      { text: 'Import journal entries from word, text, or excel files', included: false },
    ],
    buttonText: 'Get Started',
    priceId: null
  },
  {
    name: 'Premium',
    price: '$9.99',
    period: 'per month',
    features: [
      { text: 'Unlimited journal entries', included: true },
      { text: 'Strong encryption for all entries', included: true },
      { text: 'Advanced mood tracking', included: true },
      { text: 'Tags for organization', included: true },
      { text: 'Unlimited AI chats', included: true },
      { text: 'Custom fields', included: true },
      { text: 'Priority support', included: true },
      { text: 'Import journal entries from word, text, or excel files', included: true },
    ],
    buttonText: 'Upgrade to Premium',
    isPopular: true,
    priceId: env.STRIPE_PRICE_ID_PREMIUM,
  }
];

const PricingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const handlePricingClick = async (tier: PricingTier) => {
    if (!user) {
      navigate('/register');
    } else {
      if (tier.priceId) {
        try {
          // Create checkout session
          const response = await fetch(`${env.API_URL}/subscription/create-checkout-session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            },
            body: JSON.stringify({
              priceId: tier.priceId,
              userId: user.id
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to create checkout session');
          }

          const { sessionId } = await response.json();

          // Redirect to checkout
          const stripe = await stripePromise;
          if (!stripe) {
            throw new Error('Failed to load Stripe');
          }

          const { error } = await stripe.redirectToCheckout({
            sessionId
          });

          if (error) {
            throw error;
          }
        } catch (error) {
          console.error('Error initiating checkout:', error);
          toast({
            title: 'Checkout Failed',
            description: 'Could not initiate checkout. Please try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      }
    }
  };

  const pricingBgColor = useColorModeValue('white', 'gray.800');
  const pricingBorderColor = useColorModeValue('gray.200', 'gray.700');
  const pricingTextColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Container maxW="container.xl" py={20}>
      <Stack spacing={12} align="center">
        <Stack spacing={4} textAlign="center">
          <Heading size="2xl">Simple, Transparent Pricing</Heading>
          <Text fontSize="xl" color={pricingTextColor}>
            Choose the plan that best fits your journaling needs
          </Text>
        </Stack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} width="full">
          {pricingTiers.map((tier) => (
            <Box
              key={tier.name}
              bg={pricingBgColor}
              border="2px"
              borderColor={tier.isPopular ? 'blue.400' : pricingBorderColor}
              rounded="lg"
              shadow="lg"
              position="relative"
            >
              {tier.isPopular && (
                <Box
                  position="absolute"
                  top="-4"
                  right="-4"
                  bg="blue.400"
                  color="white"
                  rounded="full"
                  px={3}
                  py={1}
                  fontSize="sm"
                  fontWeight="bold"
                >
                  Most Popular
                </Box>
              )}

              <Box p={8}>
                <Stack spacing={4} align="center" textAlign="center">
                  <Heading size="lg">{tier.name}</Heading>
                  <HStack spacing={1} align="center" justify="center">
                    <Text fontSize="4xl" fontWeight="bold">
                      {tier.price}
                    </Text>
                    <Text fontSize="xl" color={pricingTextColor}>
                      /{tier.period}
                    </Text>
                  </HStack>

                  <VStack
                    spacing={3}
                    align="stretch"
                    width="full"
                    mt={8}
                  >
                    {tier.features.map((feature, index) => (
                      <HStack key={index} spacing={4}>
                        <List>
                          <ListIcon
                            as={feature.included ? FaCheck : FaTimes}
                            color={feature.included ? 'green.500' : 'red.500'}
                            w={5}
                            h={5}
                          /></List>
                        <Text>{feature.text}</Text>
                      </HStack>
                    ))}
                  </VStack>

                  <Button
                    mt={8}
                    w="full"
                    colorScheme={tier.isPopular ? 'blue' : 'gray'}
                    variant={tier.isPopular ? 'solid' : 'outline'}
                    size="lg"
                    onClick={() => handlePricingClick(tier)}
                  >
                    {tier.buttonText}
                  </Button>
                </Stack>
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      </Stack>
    </Container>
  );
};

export default PricingPage; 